import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Paper, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle, Cancel } from '@material-ui/icons';

interface CoverageItem {
  key: string;
  label: string;
  value: string;
  visible: boolean;
}

interface CoverageSectionProps {
  title: string;
  visible: boolean;
  items: CoverageItem[];
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: '1px solid #ddd',
    borderRadius: 8,
    maxHeight: '300px',
    minHeight: '100px',
    overflowY: 'auto',
    boxSizing: 'border-box',
    backgroundColor: '#f9f9f9',
    '&::-webkit-scrollbar': {
      width: 5,
    },
    '&::-webkit-scrollbar-track': {
      background: '#ddd',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
      borderRadius: 4,
      minHeight: 40,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.primary.dark,
    },
  },
  title: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  listItem: {
    backgroundColor: '#fff',
    borderRadius: 6,
    marginBottom: theme.spacing(1),
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  icon: {
    color: theme.palette.success.main,
  },
  iconCancel: {
    color: theme.palette.error.main,
  },
}));

const CoverageSection: React.FC<CoverageSectionProps> = ({ title, visible, items }) => {
  const classes = useStyles();

  if (!visible) return null;

  return (
    <Box className={classes.container}>
      <Typography variant="h6" className={classes.title}>
        {title}
      </Typography>
      <List>
        {items
          .filter((item) => item.visible)
          .map((item, index) => (
            <Paper key={item.key} elevation={2} className={classes.listItem}>
              {item.value.toLocaleLowerCase() !== 'no' &&
              item.value.toLocaleLowerCase() !== 'not' &&
              item.value.toLocaleLowerCase() !== 'x' &&
              item.value.toLocaleLowerCase() !== 'X' ? (
                <CheckCircle className={classes.icon} />
              ) : (
                <Cancel className={classes.iconCancel} />
              )}
              <ListItemText primary={item.label} secondary={item.value} />
              {index !== items.length - 1 && <Divider />}
            </Paper>
          ))}
      </List>
    </Box>
  );
};

export default CoverageSection;
